import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Video,
  Shield,
  Users,
  MonitorUp,
  MessageSquare,
  Presentation,
  Film,
  Sparkles,
  Mic,
  MicOff,
  PhoneOff,
} from 'lucide-react';
import { config } from '@/config/env';

const features = [
  { icon: Video, title: 'Crystal-clear HD', desc: '720p/1080p video on a global low-latency SFU.', grad: 'from-blue-500 to-indigo-500' },
  { icon: Users, title: 'Up to 1000 people', desc: 'Gallery, speaker & spotlight views with breakout rooms.', grad: 'from-violet-500 to-purple-500' },
  { icon: MonitorUp, title: 'Screen sharing', desc: 'Share a screen, window or tab — with live annotation.', grad: 'from-fuchsia-500 to-pink-500' },
  { icon: Presentation, title: 'Whiteboard', desc: 'Sketch together and export to PNG or PDF instantly.', grad: 'from-rose-500 to-orange-500' },
  { icon: Film, title: 'Cloud recording', desc: 'Record meetings to the cloud and share in one click.', grad: 'from-amber-500 to-yellow-500' },
  { icon: MessageSquare, title: 'Rich chat', desc: 'Reactions, GIFs, files, typing & read receipts.', grad: 'from-emerald-500 to-teal-500' },
  { icon: Shield, title: 'Enterprise security', desc: 'JWT auth, MFA, waiting rooms & host controls.', grad: 'from-cyan-500 to-blue-500' },
  { icon: Sparkles, title: 'Just works', desc: 'No installs for guests — join from any browser.', grad: 'from-indigo-500 to-sky-500' },
];

const stats = [
  { value: '1080p', label: 'HD video' },
  { value: '1000+', label: 'Participants' },
  { value: '<150ms', label: 'Latency' },
  { value: '99.9%', label: 'Uptime' },
];

export default function LandingPage(): JSX.Element {
  return (
    <div className="relative min-h-full overflow-hidden bg-gradient-to-b from-indigo-50 via-white to-white dark:from-slate-950 dark:via-slate-950 dark:to-slate-900">
      {/* decorative blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-brand-400/30 blur-3xl dark:bg-brand-600/20" />
        <div className="absolute right-0 top-20 h-96 w-96 rounded-full bg-fuchsia-400/20 blur-3xl dark:bg-fuchsia-600/10" />
        <div className="absolute bottom-0 left-1/3 h-96 w-96 rounded-full bg-cyan-400/20 blur-3xl dark:bg-cyan-600/10" />
      </div>

      {/* header */}
      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2 text-xl font-extrabold">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-indigo-600 text-white shadow-lg shadow-brand-500/30">
            <Video size={18} />
          </span>
          {config.appName}
        </div>
        <nav className="flex items-center gap-2 sm:gap-4">
          <Link to="/pricing" className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:text-brand-600 dark:text-slate-300">Pricing</Link>
          <Link to="/login" className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:text-brand-600 dark:text-slate-300">Log in</Link>
          <Link to="/register" className="rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition hover:shadow-xl hover:shadow-brand-500/40">
            Get started
          </Link>
        </nav>
      </header>

      {/* hero */}
      <section className="relative z-10 mx-auto max-w-6xl px-6 pt-12 text-center sm:pt-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white/70 px-4 py-1.5 text-sm font-medium text-brand-700 shadow-sm backdrop-blur dark:border-brand-800 dark:bg-slate-900/70 dark:text-brand-300"
        >
          <Sparkles size={14} /> HD video · recording · whiteboard — all in the browser
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mx-auto max-w-4xl text-5xl font-black leading-tight tracking-tight sm:text-7xl"
        >
          Meetings that just{' '}
          <span className="bg-gradient-to-r from-brand-600 via-indigo-500 to-fuchsia-500 bg-clip-text text-transparent">
            work
          </span>
          .
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mx-auto mt-6 max-w-2xl text-lg text-slate-600 dark:text-slate-400"
        >
          Enterprise-grade video conferencing with HD video, screen sharing, recording,
          whiteboards and breakout rooms — no downloads required.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row"
        >
          <Link to="/register" className="rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 px-8 py-3.5 text-base font-semibold text-white shadow-xl shadow-brand-500/30 transition hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-brand-500/40">
            Start for free
          </Link>
          <Link to="/join" className="rounded-xl border border-slate-300 bg-white/70 px-8 py-3.5 text-base font-semibold text-slate-700 backdrop-blur transition hover:border-brand-400 hover:text-brand-600 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200">
            Join a meeting
          </Link>
        </motion.div>

        {/* product preview mockup */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.25, type: 'spring', stiffness: 60 }}
          className="mx-auto mt-16 max-w-4xl"
        >
          <HeroMockup />
        </motion.div>
      </section>

      {/* stats */}
      <section className="relative z-10 mx-auto mt-20 grid max-w-4xl grid-cols-2 gap-6 px-6 sm:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="text-center">
            <p className="bg-gradient-to-r from-brand-600 to-fuchsia-500 bg-clip-text text-3xl font-black text-transparent">{s.value}</p>
            <p className="mt-1 text-sm text-slate-500">{s.label}</p>
          </div>
        ))}
      </section>

      {/* features */}
      <section className="relative z-10 mx-auto mt-24 max-w-6xl px-6">
        <h2 className="text-center text-4xl font-extrabold tracking-tight">Everything you need to meet</h2>
        <p className="mx-auto mt-3 max-w-xl text-center text-slate-600 dark:text-slate-400">
          One platform for video, collaboration and team communication.
        </p>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 4) * 0.05 }}
              className="group rounded-2xl border border-slate-200/70 bg-white/80 p-6 shadow-sm backdrop-blur transition hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900/70"
            >
              <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${f.grad} text-white shadow-lg`}>
                <f.icon size={22} />
              </div>
              <h3 className="text-lg font-bold">{f.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 mx-auto mt-24 max-w-6xl px-6">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-brand-600 via-indigo-600 to-fuchsia-600 px-8 py-16 text-center shadow-2xl">
          <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-10 -left-10 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
          <h2 className="relative text-4xl font-black text-white">Ready to start meeting?</h2>
          <p className="relative mx-auto mt-3 max-w-lg text-indigo-100">
            Create your free account and host your first HD meeting in under a minute.
          </p>
          <Link to="/register" className="relative mt-8 inline-block rounded-xl bg-white px-8 py-3.5 text-base font-bold text-brand-700 shadow-lg transition hover:-translate-y-0.5 hover:shadow-2xl">
            Get started — it's free
          </Link>
        </div>
      </section>

      {/* footer */}
      <footer className="relative z-10 mx-auto mt-20 max-w-6xl border-t border-slate-200 px-6 py-10 text-sm text-slate-500 dark:border-slate-800">
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2 font-bold text-slate-700 dark:text-slate-300">
            <Video size={16} className="text-brand-600" /> {config.appName}
          </div>
          <p>© {config.appName}. Built for teams that move fast.</p>
          <div className="flex gap-4">
            <Link to="/pricing" className="hover:text-brand-600">Pricing</Link>
            <Link to="/login" className="hover:text-brand-600">Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** A stylized in-product preview (fake meeting grid) for the hero. */
function HeroMockup(): JSX.Element {
  const tiles = [
    { name: 'You', grad: 'from-blue-400 to-indigo-500', muted: false },
    { name: 'Sarah', grad: 'from-fuchsia-400 to-pink-500', muted: true },
    { name: 'Mike', grad: 'from-emerald-400 to-teal-500', muted: false },
    { name: 'Aisha', grad: 'from-amber-400 to-orange-500', muted: false },
  ];
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-900 shadow-2xl shadow-brand-900/20 ring-1 ring-black/5 dark:border-slate-800">
      {/* top bar */}
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-200">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" /> Team Standup
        </div>
        <div className="flex gap-1.5">
          <span className="h-3 w-3 rounded-full bg-red-400" />
          <span className="h-3 w-3 rounded-full bg-amber-400" />
          <span className="h-3 w-3 rounded-full bg-green-400" />
        </div>
      </div>
      {/* grid */}
      <div className="grid grid-cols-2 gap-2 bg-slate-950 p-3">
        {tiles.map((t) => (
          <div key={t.name} className={`relative flex aspect-video items-center justify-center rounded-xl bg-gradient-to-br ${t.grad}`}>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/25 text-lg font-bold text-white backdrop-blur">
              {t.name[0]}
            </div>
            <span className="absolute bottom-2 left-2 rounded-md bg-black/40 px-1.5 py-0.5 text-xs text-white backdrop-blur">{t.name}</span>
            <span className="absolute bottom-2 right-2 text-white">
              {t.muted ? <MicOff size={14} /> : <Mic size={14} />}
            </span>
          </div>
        ))}
      </div>
      {/* toolbar */}
      <div className="flex items-center justify-center gap-3 border-t border-slate-800 bg-slate-900 py-3">
        {[Mic, Video, MonitorUp, MessageSquare, Users].map((Icon, i) => (
          <span key={i} className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-800 text-slate-300">
            <Icon size={16} />
          </span>
        ))}
        <span className="flex h-9 items-center gap-1.5 rounded-lg bg-red-600 px-3 text-xs font-medium text-white">
          <PhoneOff size={14} /> Leave
        </span>
      </div>
    </div>
  );
}
